import React, { useState, useEffect } from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Tooltip,
  Box,
  TextField,
  Alert,
  IconButton,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "notistack";
import { makeDotykackaRefreshToken } from "app/services/api/integrations";
import { getDotykackaBranch, getDotykackaProducts } from "app/services/api/management";

/**
 * Sekcia pre zariadenie typu "external" (Externé zariadenie) — DSOK-442.
 *
 * Namiesto POS/eKasa definuje tok objednávky na tomto zariadení:
 *  - payment.type:   none (žiadna – už zaplatené externe) | albacon (hotovosť cez automat)
 *  - downstream.type: none | dotykacka | ikelp | oberon
 *
 * Externé objednávky majú VLASTNÉ (device-level) Dotykačka nastavenia, nezávislé od
 * customer-level `integrations.orderManagement`. Connect URL / Načítať pobočky / auto-filled
 * cloudId+refreshToken fungujú rovnako ako v integrácii, ale cez device-scoped endpointy
 * (OAuth callback uloží creds na zariadenie).
 *
 * Ukladá sa na `customer.devices.${device}.externalOrders.{payment,downstream}.*`.
 * Nápovedy k poliam sú ako hover MUI Tooltip (projektové pravidlo).
 */
const ExternalDeviceSection = ({ device, setFieldValue, values, t }) => {
  const { enqueueSnackbar } = useSnackbar();
  const deviceObj = values?.customer?.devices?.[device] || {};
  const ext = deviceObj?.externalOrders || {};
  const paymentType = ext?.payment?.type ?? "none";
  const downstreamType = ext?.downstream?.type ?? "none";

  // External orders always use their OWN Dotykačka credentials (device-level),
  // independent of the customer-level integrations.orderManagement config.
  const dotykacka = ext?.downstream?.dotykacka || {};
  const dotyCloudId = dotykacka?.cloudId ?? "";
  const dotyRefreshToken = dotykacka?.refreshToken ?? "";
  const dotyBranchId = dotykacka?.branchId ?? "";

  // Which customer fields are forwarded into the POS (downstream) note. Configurable per
  // device, same for every POS system. Unset → all ON (matches backend default).
  const noteFields = ext?.downstream?.noteFields || {};
  const noteFieldOn = (key) => noteFields?.[key] !== false;
  const NOTE_FIELDS = [
    { key: "name", label: "Meno", help: "Posielať meno zákazníka do POS objednávky." },
    { key: "phone", label: "Telefón", help: "Posielať telefónne číslo zákazníka do POS objednávky." },
    { key: "address", label: "Adresa doručenia", help: "Posielať doručovaciu adresu do POS objednávky." },
    { key: "paymentStatus", label: "Stav úhrady", help: "Posielať do POS informáciu, či je objednávka uhradená (UHRADENÉ/NEUHRADENÉ)." },
  ];

  const customerId = values?.customer?.customerId;
  const beDomain = values?.customer?.beDomain;

  const [dotykackaGenerateTokenUrl, setDotykackaGenerateTokenUrl] = useState("");
  const [dotykackaBranches, setDotykackaBranches] = useState();
  const [dotykackaProducts, setDotykackaProducts] = useState();
  const [fetching, setFetching] = useState(false);
  const [fetchingProducts, setFetchingProducts] = useState(false);

  // Shipping / delivery item sent to Dotykačka for external orders (toggle + Dotykačka product).
  const shipping = ext?.shipping || {};
  const shippingEnabled = shipping?.enabled === true;
  const shippingProduct = shipping?.product || {};
  const shippingProductId =
    shippingProduct?.id != null ? String(shippingProduct.id) : "";

  // Connect URL (merchant register) — same per-customer link as the integration. The
  // OAuth callback also mirrors the resulting creds onto this external device.
  useEffect(() => {
    const isDotykacka = downstreamType === "dotykacka";
    if (!isDotykacka || !customerId) {
      setDotykackaGenerateTokenUrl("");
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const result = await makeDotykackaRefreshToken(customerId, beDomain);
        if (isMounted) {
          setDotykackaGenerateTokenUrl(result?.redirectUrl || "");
        }
      } catch (error) {
        if (isMounted) setDotykackaGenerateTokenUrl("");
        console.log("Error creating Dotykacka refresh token URL (device):", error);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [downstreamType, customerId, beDomain]);

  const handleCopyDotykackaRedirectUrl = async () => {
    if (!dotykackaGenerateTokenUrl) return;
    try {
      await navigator.clipboard.writeText(dotykackaGenerateTokenUrl);
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.dotykacka.redirectUrlCopied", {
          defaultValue: "Redirect URL skopírovaná",
        }),
        { variant: "success" },
      );
    } catch (error) {
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.dotykacka.redirectUrlCopyError", {
          defaultValue: "Kopírovanie zlyhalo",
        }),
        { variant: "error" },
      );
    }
  };

  return (
    <>
      <Grid item xs={12} md={12} lg={12}>
        <Typography sx={{ mt: 2 }}>
          {t("pages.dsokDetails.externalDevice.title", {
            defaultValue: "Externé zariadenie – tok objednávky",
          })}
        </Typography>
        <Divider />
      </Grid>

      <Grid item xs={12} md={5} lg={4}>
        <Tooltip
          arrow
          title={t("pages.dsokDetails.externalDevice.paymentTypeHelp", {
            defaultValue:
              "Ako sa objednávka na tomto zariadení uhrádza. Žiadna = už zaplatené v externom systéme; Albacon = hotovosť cez Albacon automat.",
          })}
        >
          <Box component="span" sx={{ display: "block" }}>
            <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
              <InputLabel id={`ext-payment-${device}`}>
                {t("pages.dsokDetails.externalDevice.paymentType", {
                  defaultValue: "Typ platby",
                })}
              </InputLabel>
              <Select
                labelId={`ext-payment-${device}`}
                label={t("pages.dsokDetails.externalDevice.paymentType", {
                  defaultValue: "Typ platby",
                })}
                value={paymentType}
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.externalOrders.payment.type`,
                    e.target.value,
                  )
                }
              >
                <MenuItem value="none">
                  {t("pages.dsokDetails.externalDevice.paymentNone", {
                    defaultValue: "Žiadna",
                  })}
                </MenuItem>
                <MenuItem value="albacon">
                  {t("pages.dsokDetails.externalDevice.paymentAlbacon", {
                    defaultValue: "Albacon",
                  })}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Tooltip>
      </Grid>

      <Grid item xs={12} md={5} lg={4}>
        <Tooltip
          arrow
          title={t("pages.dsokDetails.externalDevice.downstreamHelp", {
            defaultValue:
              "Kam sa objednávka po prijatí pošle ďalej (POS). Žiadny = ostane len u nás.",
          })}
        >
          <Box component="span" sx={{ display: "block" }}>
            <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
              <InputLabel id={`ext-downstream-${device}`}>
                {t("pages.dsokDetails.externalDevice.downstream", {
                  defaultValue: "Downstream (POS)",
                })}
              </InputLabel>
              <Select
                labelId={`ext-downstream-${device}`}
                label={t("pages.dsokDetails.externalDevice.downstream", {
                  defaultValue: "Downstream (POS)",
                })}
                value={downstreamType}
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.externalOrders.downstream.type`,
                    e.target.value,
                  )
                }
              >
                <MenuItem value="none">
                  {t("pages.dsokDetails.externalDevice.downstreamNone", {
                    defaultValue: "Žiadny",
                  })}
                </MenuItem>
                <MenuItem value="dotykacka">Dotykačka</MenuItem>
                {/* iKelp/Oberon downstream not ready yet → disabled, only Dotykačka is selectable. */}
                <MenuItem value="ikelp" disabled>
                  {t("pages.dsokDetails.externalDevice.downstreamComingSoon", {
                    defaultValue: "{{name}} (pripravujeme)",
                    name: "iKelp",
                  })}
                </MenuItem>
                <MenuItem value="oberon" disabled>
                  {t("pages.dsokDetails.externalDevice.downstreamComingSoon", {
                    defaultValue: "{{name}} (pripravujeme)",
                    name: "Oberon",
                  })}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Tooltip>
      </Grid>

      {/* Downstream note fields — which customer data is forwarded into the POS order.
          Shown once any POS (downstream) is selected; applies to every POS system. */}
      {downstreamType !== "none" && (
        <>
          <Grid item xs={12} md={12} lg={12}>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {t("pages.dsokDetails.externalDevice.noteFieldsTitle", {
                defaultValue: "Údaje posielané do POS (Downstream)",
              })}
            </Typography>
          </Grid>
          {NOTE_FIELDS.map((f) => (
            <Grid item xs={6} md={3} lg={3} key={f.key}>
              <Tooltip
                arrow
                title={t(
                  `pages.dsokDetails.externalDevice.noteField_${f.key}Help`,
                  { defaultValue: f.help },
                )}
              >
                <Box component="span" sx={{ display: "block" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={noteFieldOn(f.key)}
                        onChange={(e) =>
                          setFieldValue(
                            `customer.devices.${device}.externalOrders.downstream.noteFields.${f.key}`,
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={t(
                      `pages.dsokDetails.externalDevice.noteField_${f.key}`,
                      { defaultValue: f.label },
                    )}
                  />
                </Box>
              </Tooltip>
            </Grid>
          ))}
        </>
      )}

      {/* Dotykačka downstream — external orders use their OWN device-level credentials
          (connect URL / branches / auto-filled cloudId+refreshToken), mirroring the
          integration but stored on the device via device-scoped endpoints. */}
      {downstreamType === "dotykacka" && (
        <>
          <Grid item xs={12} md={12} lg={12}>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {t("pages.dsokDetails.externalDevice.dotykackaTitle", {
                defaultValue: "Dotykačka – prístup pre externé objednávky",
              })}
            </Typography>
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <Alert variant="outlined" severity="info" sx={{ mt: 1, mb: 1 }}>
              <Box
                component="ul"
                sx={{ m: 0, pl: 2.5, "& li": { typography: "body2" } }}
              >
                <Box component="li">
                  {t("pages.dsokDetails.integrations.dotykacka.redirectUrlHelp", {
                    defaultValue:
                      "Otvorte redirect URL a autorizujte prístup v Dotykačke.",
                  })}
                </Box>
                <Box component="li" sx={{ mt: 1 }}>
                  {t(
                    "pages.dsokDetails.integrations.dotykacka.autoGeneratedFieldsHelp",
                    {
                      defaultValue:
                        "Cloud ID a Refresh token sa doplnia automaticky po autorizácii (načítajte zákazníka znova).",
                    },
                  )}
                </Box>
              </Box>
            </Alert>
          </Grid>

          {dotykackaGenerateTokenUrl ? (
            <Grid item xs={12} md={12} lg={12}>
              <Box
                sx={{
                  mt: 0,
                  mb: 1,
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={t(
                    "pages.dsokDetails.integrations.dotykacka.redirectUrlLabel",
                    { defaultValue: "Redirect URL" },
                  )}
                  value={dotykackaGenerateTokenUrl}
                  InputProps={{ readOnly: true }}
                />
                <Tooltip
                  title={t(
                    "pages.dsokDetails.integrations.dotykacka.copyRedirectUrl",
                    { defaultValue: "Kopírovať" },
                  )}
                >
                  <IconButton
                    aria-label={t(
                      "pages.dsokDetails.integrations.dotykacka.copyRedirectUrl",
                      { defaultValue: "Kopírovať" },
                    )}
                    size="small"
                    onClick={() => void handleCopyDotykackaRedirectUrl()}
                    sx={{ mt: 1 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          ) : null}

          <Grid item xs={12} md={12} lg={3}>
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1, boxShadow: "none" }}
              disabled={fetching}
              onClick={async () => {
                try {
                  setFetching(true);
                  const branchResult = await getDotykackaBranch(
                    customerId,
                    beDomain,
                  );
                  setDotykackaBranches(branchResult?.branches);
                } catch (error) {
                  console.log("Error (device branches):", error);
                } finally {
                  setFetching(false);
                }
              }}
            >
              {t("pages.dsokDetails.getBranch", {
                defaultValue: "Načítať pobočky",
              })}
            </Button>
          </Grid>

          {Array.isArray(dotykackaBranches) && dotykackaBranches.length > 0 && (
            <Grid item xs={12} md={5} lg={4}>
              <TextField
                select
                fullWidth
                sx={{ mt: 1, mb: 1 }}
                label={t("pages.dsokDetails.branch", { defaultValue: "Pobočka" })}
                value={dotyBranchId}
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.externalOrders.downstream.dotykacka.branchId`,
                    e.target.value,
                  )
                }
              >
                {dotykackaBranches.map((branch, index) => {
                  const isObject =
                    typeof branch === "object" && branch !== null;
                  const value = isObject
                    ? branch.id ||
                      branch.branchId ||
                      branch.code ||
                      branch.name ||
                      index
                    : branch;
                  const label = isObject
                    ? branch.name ||
                      branch.label ||
                      branch.title ||
                      branch.id ||
                      String(value)
                    : String(branch);
                  return (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12} md={12} lg={12}></Grid>

          <Grid item xs={12} md={6} lg={6}>
            <Tooltip
              arrow
              title={t("pages.dsokDetails.externalDevice.dotykackaCloudIdHelp", {
                defaultValue:
                  "Cloud ID Dotykačka prevádzky pre externé objednávky (doplní sa automaticky po autorizácii).",
              })}
            >
              <Box component="span" sx={{ display: "block" }}>
                <TextField
                  fullWidth
                  sx={{ mt: 1, mb: 1 }}
                  label={t("pages.dsokDetails.cloudId", {
                    defaultValue: "Cloud ID",
                  })}
                  value={dotyCloudId}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Tooltip>
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <Tooltip
              arrow
              title={t(
                "pages.dsokDetails.externalDevice.dotykackaRefreshTokenHelp",
                {
                  defaultValue:
                    "Refresh token Dotykačka API (doplní sa automaticky po autorizácii).",
                },
              )}
            >
              <Box component="span" sx={{ display: "block" }}>
                <TextField
                  fullWidth
                  sx={{ mt: 1, mb: 1 }}
                  label={t("pages.dsokDetails.externalDevice.dotykackaRefreshToken", {
                    defaultValue: "Refresh token",
                  })}
                  value={dotyRefreshToken}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Tooltip>
          </Grid>

          {/* Shipping / delivery item — when enabled, a Dotykačka product is sent to Dotykačka
              as a delivery line priced from WooCommerce. */}
          <Grid item xs={12} md={12} lg={12}>
            <Divider sx={{ my: 1 }} />
            <Tooltip
              arrow
              title={t("pages.dsokDetails.externalDevice.shippingEnabledHelp", {
                defaultValue:
                  "Ak je zapnuté, cena dopravy z objednávky sa pošle do Dotykačky ako samostatná položka (vybraný Dotykačka produkt nižšie). Ak je vypnuté, doprava sa ignoruje.",
              })}
            >
              <Box component="span">
                <FormControlLabel
                  control={
                    <Switch
                      checked={shippingEnabled}
                      onChange={(e) =>
                        setFieldValue(
                          `customer.devices.${device}.externalOrders.shipping.enabled`,
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={t("pages.dsokDetails.externalDevice.shippingEnabled", {
                    defaultValue: "Pridať položku dopravy",
                  })}
                />
              </Box>
            </Tooltip>
          </Grid>

          {shippingEnabled && (
            <>
              <Grid item xs={12} md={12} lg={3}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, boxShadow: "none" }}
                  disabled={fetchingProducts}
                  onClick={async () => {
                    try {
                      setFetchingProducts(true);
                      const res = await getDotykackaProducts(
                        customerId,
                        beDomain,
                      );
                      setDotykackaProducts(res?.products || []);
                    } catch (error) {
                      console.log("Error (dotykacka products):", error);
                    } finally {
                      setFetchingProducts(false);
                    }
                  }}
                >
                  {t("pages.dsokDetails.externalDevice.loadDotykackaProducts", {
                    defaultValue: "Načítať produkty",
                  })}
                </Button>
              </Grid>

              <Grid item xs={12} md={7} lg={6}>
                <Tooltip
                  arrow
                  title={t(
                    "pages.dsokDetails.externalDevice.shippingProductHelp",
                    {
                      defaultValue:
                        "Dotykačka produkt, ktorý sa pošle ako položka dopravy (cena príde z objednávky).",
                    },
                  )}
                >
                  <Box component="span" sx={{ display: "block" }}>
                    <TextField
                      select
                      fullWidth
                      sx={{ mt: 1, mb: 1 }}
                      label={t(
                        "pages.dsokDetails.externalDevice.shippingProduct",
                        { defaultValue: "Položka dopravy (Dotykačka)" },
                      )}
                      value={shippingProductId}
                      onChange={(e) => {
                        const id = e.target.value;
                        const found = (dotykackaProducts || []).find(
                          (p) => String(p.id) === String(id),
                        );
                        setFieldValue(
                          `customer.devices.${device}.externalOrders.shipping.product`,
                          {
                            id: String(id),
                            name: found?.name || shippingProduct?.name || "",
                            vatRate:
                              found?.vatRate != null
                                ? found.vatRate
                                : shippingProduct?.vatRate ?? 0,
                          },
                        );
                      }}
                    >
                      {/* Keep the stored product visible even before the list is loaded. */}
                      {!(
                        Array.isArray(dotykackaProducts) &&
                        dotykackaProducts.length
                      ) &&
                        shippingProductId && (
                          <MenuItem value={shippingProductId}>
                            {shippingProduct?.name || shippingProductId}
                          </MenuItem>
                        )}
                      {(dotykackaProducts || []).map((p) => (
                        <MenuItem key={String(p.id)} value={String(p.id)}>
                          {p.name || String(p.id)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Tooltip>
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );
};

export default ExternalDeviceSection;
